import {
  Link,
  StatusIndicator,
  ButtonDropdown,
  SpaceBetween,
  Button,
  ButtonDropdownProps,
} from '@cloudscape-design/components';
import _, { round, toLower } from 'lodash';
import {
  AUDIT_STATUS_LABELS,
  AUDIT_TYPE_LABELS,
  AuditStatus,
  AuditTypeOptions,
} from 'src/contstant/commonLabels';
import { Audit } from 'src/types/helios/Audit';
import {
  findBusinessUnitById,
  formatDateTime,
  getAuditStatusColor,
  getAuditStatusIcon,
} from 'src/utils/generalUtils';
import { ExportFileConfig } from 'src/utils/exportFileUtils';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import routes from 'src/contstant/routePaths';
import { getAssigneesDisplayValue } from 'src/helper/performAuditHelpers';
export const defaultVisibleColumns = [
  'name',
  'auditId',
  'location',
  'businessUnit',
  'divisionName',
  'templateName',
  'auditType',
  'status',
  'assignedTo',
  'expiryDate',
  'startDate',
  'createdBy',
  'createdAt',
  'scorePercent',
  'actions',
  'lastUpdatedAt',
  'lastUpdatedBy',
];

export const getPerformAuditUrl = (item: Audit) => {
  const params = new URLSearchParams({
    auditId: item.auditId,
    businessUnit: item.businessUnit || item.businessUnitId || '',
  });
  return `${routes.PERFORM_AUDIT.path}?${params}`;
};

export const getAudiSummaryUrl = (item: Audit) => {
  const params = new URLSearchParams({
    auditId: item.auditId,
    businessUnit: item.businessUnit || item.businessUnitId || '',
  });
  return `${routes.AUDIT_SUMMARY.path}?${params}`;
};

export const getAuditsTableDefinition = (
  viewAuditSummary: (item: Audit) => void,
  performAudit: (item: Audit) => void,
  cancelAudit: (item: Audit) => void,
  permissions: Record<
    'edit' | 'perform',
    Record<'calibration' | 'progress' | 'situation' | 'mpa' | 'test', boolean>
  >,
) => {
  return [
    {
      id: 'actions',
      header: 'Action',
      cell: (item: Audit) => {
        const statusCheck =
          item.status === AuditStatus.IN_PROGRESS ||
          item.status === AuditStatus.NOT_STARTED;

        const checkStatusCancel =
          item.status === AuditStatus.COMPLETED || item.status === AuditStatus.CANCELLED;
        const disabledEdit =
          (item.auditType === AuditTypeOptions.CALIBRATION &&
            !permissions.edit.calibration) ||
          (item.auditType === AuditTypeOptions.PROGRESS && !permissions.edit.progress) ||
          (item.auditType === AuditTypeOptions.SITUATION &&
            !permissions.edit.situation) ||
          (item.auditType === AuditTypeOptions.MPA && !permissions.edit.mpa) ||
          (item.auditType === AuditTypeOptions.TEST && !permissions.edit.test);
        const disabledPerform =
          (item.auditType === AuditTypeOptions.CALIBRATION &&
            !permissions.perform.calibration) ||
          (item.auditType === AuditTypeOptions.PROGRESS &&
            !permissions.perform.progress) ||
          (item.auditType === AuditTypeOptions.SITUATION &&
            !permissions.perform.situation) ||
          (item.auditType === AuditTypeOptions.MPA && !permissions.perform.mpa) ||
          (item.auditType === AuditTypeOptions.TEST && !permissions.perform.test);
        const descriptionEdit = disabledEdit
          ? 'You do not have permission to edit this'
          : '';
        const descriptionPerform = disabledPerform
          ? 'You do not have permission to perform this'
          : '';
        return (
          <SpaceBetween size={'s'}>
            <ButtonDropdown
              data-testid="option-button-dropdown"
              expandToViewport={true}
              items={[
                {
                  text: 'View',
                  id: 'viewSummary',
                  href: getAudiSummaryUrl(item),
                },
                {
                  text: 'Perform Audit',
                  id: 'perform',
                  disabled: disabledPerform || !statusCheck,
                  disabledReason: descriptionPerform,
                  href: getPerformAuditUrl(item),
                },
                {
                  text: 'Cancel Audit',
                  id: 'cancel',
                  disabled: disabledEdit || checkStatusCancel,
                  disabledReason: descriptionEdit,
                },
              ]}
              variant="icon"
              onItemClick={(e: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
                e.preventDefault();
                if (e.detail.id === 'viewSummary') {
                  viewAuditSummary(item);
                  return false;
                }
                if (e.detail.id === 'perform') {
                  performAudit(item);
                }
                if (e.detail.id === 'cancel') {
                  cancelAudit(item);
                }
              }}
            />
          </SpaceBetween>
        );
      },
    },
    {
      id: 'auditId',
      header: 'Audit ID',
      cell: (item: Audit) => item.auditId,
    },
    {
      id: 'name',
      header: 'Audit Name',
      maxWidth: 500,
      minWidth: 300,
      cell: (item: Audit) => (
        <div className="title-container">
          <Button
            ariaLabel="View audit summary (opens new tab)"
            href={getAudiSummaryUrl(item)}
            iconAlign="right"
            iconName="external"
            target="_blank"
            variant="icon"
          ></Button>
          <span className="title-hover" onClick={() => viewAuditSummary(item)}>
            {item.name}
          </span>
        </div>
      ),
    },
    {
      id: 'location',
      header: 'Location',
      cell: (item: Audit) => item.location,
    },
    {
      id: 'businessUnit',
      header: 'Business Unit',
      cell: (item: Audit) => item.businessUnit ?? '-',
    },
    {
      id: 'divisionName',
      header: 'Division',
      cell: (item: Audit) => item.divisionName || '-',
    },
    {
      id: 'templateId',
      header: 'Template ID',
      cell: (item: Audit) => item.templateId || '-',
    },
    {
      id: 'templateName',
      header: 'Template',
      cell: (item: Audit) => item.templateName || '-',
    },
    {
      id: 'auditType',
      header: 'Audit Type',
      cell: (item: Audit) =>
        AUDIT_TYPE_LABELS[item.auditType as keyof typeof AuditTypeOptions],
    },
    {
      id: 'status',
      header: 'Audit Status',
      cell: (item: Audit) => {
        return (
          <StatusIndicator
            type={getAuditStatusIcon(item.status)}
            colorOverride={getAuditStatusColor(item.status)}
          >
            {AUDIT_STATUS_LABELS[item.status as keyof typeof AuditStatus]}
          </StatusIndicator>
        );
      },
    },
    {
      id: 'assignedTo',
      header: 'Assigned To',
      cell: (item: Audit) => getAssigneesDisplayValue(item),
    },
    {
      id: 'startDate',
      header: 'Start Date',
      cell: (item: Audit) => formatDateTime(item.startDate, 'DD MMM YYYY'),
    },
    {
      id: 'expiryDate',
      header: 'Expiry Date',
      cell: (item: Audit) =>
        item.expirationDate ? formatDateTime(item.expirationDate, 'DD MMM YYYY') : '-',
    },
    {
      id: 'scorePercent',
      header: 'Audit Score',
      cell: (item: Audit) =>
        item?.metrics?.scorePercent !== undefined && item.status === AuditStatus.COMPLETED
          ? `${round(item?.metrics?.scorePercent, 2)}%`
          : '-',
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: Audit) => item.createdBy || '-',
    },
    {
      id: 'createdAt',
      header: 'Created At',
      cell: (item: Audit) => formatDateTime(item.createdDate),
    },
    {
      id: 'lastUpdatedBy',
      header: 'Updated By',
      cell: (item: Audit) => item.lastUpdatedBy || '-',
    },
    {
      id: 'lastUpdatedAt',
      header: 'Updated At',
      cell: (item: Audit) => formatDateTime(item.lastUpdatedDate),
    },
  ];
};

export const exportAuditsConfig: ExportFileConfig = {
  fileName: 'audits_list.csv',
  mappings: {
    'Audit ID': (audit: Audit) => audit.auditId,
    'Audit Name': (audit: Audit) => audit.name,
    'Location': (audit: Audit) => audit.location,
    'Business Unit': (audit: Audit) => audit.businessUnit || '-',
    'Division': (audit: Audit) => audit.divisionName || '-',
    'Template ID': (audit: Audit) => audit.templateId || '-',
    'Template Name': (audit: Audit) => audit.templateName || '-',
    'Audit Type': (audit: Audit) =>
      AUDIT_TYPE_LABELS[audit.auditType as keyof typeof AUDIT_TYPE_LABELS] || '-',
    'Status': (audit: Audit) =>
      AUDIT_STATUS_LABELS[audit.status as keyof typeof AuditStatus] || '-',
    'Assigned To': (audit: Audit) => getAssigneesDisplayValue(audit),
    'Start Date': (audit: Audit) => {
      if (!audit.startDate) return '-';

      return formatDateTime(audit.startDate, 'DD MMM YYYY');
    },

    'Expiry Date': (audit: Audit) => {
      if (!audit.expirationDate) return '-';

      return formatDateTime(audit.expirationDate, 'DD MMM YYYY');
    },
    'Created By': (audit: Audit) => audit.createdBy || '-',
    'Created At': (audit: Audit) => {
      if (!audit.createdDate) return '-';

      return formatDateTime(audit.createdDate);
    },
    'Updated By': (audit: Audit) => audit.lastUpdatedBy || '-',
    'Updated At': (audit: Audit) => {
      if (!audit.lastUpdatedDate) return '-';

      return formatDateTime(audit.lastUpdatedDate);
    },
    'Audit Score': (audit: Audit) =>
      audit?.metrics?.scorePercent && audit.status === AuditStatus.COMPLETED
        ? `${round(audit?.metrics?.scorePercent, 2)}%`
        : '-',

    'Total Questions': (audit: Audit) => audit.metrics?.totalQuestions?.toString() || '-',
    'Completed Questions': (audit: Audit) =>
      audit.metrics?.completedQuestions?.toString() || '-',
    'Passed Questions': (audit: Audit) =>
      audit.metrics?.passedQuestions?.toString() || '-',
    'Failed Questions': (audit: Audit) =>
      audit.metrics?.failedQuestions?.toString() || '-',
  },
};
